import { jsx as _jsx } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import { memo, useCallback, useMemo } from "react";
import isEqual from "react-fast-compare";
import { useGetPublicAssetMenuOptionsCallback } from "~/components/PublicBoard/useGetPublicAssetMenuOptionsCallback";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { AssetTableRow } from "~/components/TableView/AssetTableRow";
import { MobileTableRowContainer } from "~/components/TableView/MobileTableRow/MobileTableRowContainer";
import { PublicAssetNameCell } from "~/components/TableView/PrivateTableView/PublicAssetNameCell";
import { PublicAssetCustomFieldTableCell } from "~/components/TableView/TableRow/CustomFieldTableCell/PublicAssetCustomFieldTableCell";
import { DesktopTableRowContainer } from "~/components/TableView/TableRow/DesktopTableRowContainer";
import { QueryParamNames } from "~/constants/search";
import { TABLE_ROW } from "~/constants/testIDs";
import { useGoToAssetPage } from "~/hooks/useGoToAssetPage";
import { useOpenAssetInNewTab } from "~/hooks/useOpenAssetInNewTab";
import { usePublicSelectionMenuOptions } from "~/hooks/usePublicSelectionMenuOptions";
import { useWorkspaceHorizontalPadding } from "~/hooks/useWorkspaceHorizontalPadding";
import { AssetModalPanel } from "~/providers/AssetModalPanelContextProvider";
import { getAssetGalleryItemType } from "~/utils/AssetUtils";
import { canChangeAssetCustomFields, canDownloadVersion } from "~/utils/permissions/assetPermissions";
export const PublicAssetTableRow = /*#__PURE__*/ memo((param)=>{
    let { data: asset, style } = param;
    const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
    const { getAssetMenuOptionsCallback } = useGetPublicAssetMenuOptionsCallback();
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const itemType = getAssetGalleryItemType(asset);
    const { permissions } = usePublicPermissions();
    const getItemMenuOptions = getAssetMenuOptionsCallback({
        item: asset
    });
    const { goToAssetPage } = useGoToAssetPage();
    const { openAssetInNewTab } = useOpenAssetInNewTab();
    const onItemClick = useCallback(()=>goToAssetPage({
            asset,
            trackLocation: "card-click"
        }), [
        asset,
        goToAssetPage
    ]);
    const onItemCmdClick = useCallback(()=>openAssetInNewTab({
            asset,
            trackLocation: "card-click"
        }), [
        asset,
        openAssetInNewTab
    ]);
    const onCommentsClick = useCallback(()=>goToAssetPage({
            asset,
            query: {
                [QueryParamNames.tab]: AssetModalPanel.COMMENTS
            },
            trackLocation: "comments-click"
        }), [
        asset,
        goToAssetPage
    ]);
    const onVersionsClick = useCallback(()=>{
        goToAssetPage({
            asset,
            query: {
                [QueryParamNames.versions]: true
            },
            trackLocation: "versions-click"
        });
    }, [
        asset,
        goToAssetPage
    ]);
    const canDownload = canDownloadVersion(permissions);
    const canEditCustomFields = canChangeAssetCustomFields(permissions);
    const isSelectable = canDownload || canEditCustomFields;
    const memoizedAssetProps = useMemo(()=>({
            getItemMenuOptions,
            getSelectionMenuOptions,
            isSelectable,
            onCommentsClick,
            onItemClick,
            onItemCmdClick,
            onVersionsClick
        }), [
        getItemMenuOptions,
        getSelectionMenuOptions,
        isSelectable,
        onCommentsClick,
        onItemClick,
        onItemCmdClick,
        onVersionsClick
    ]);
    const renderNameCell = useCallback((param)=>{
        let { isSelected, isHovering } = param;
        return /*#__PURE__*/ _jsx(PublicAssetNameCell, {
            isHovering: isHovering,
            isSelected: isSelected,
            clip: asset,
            ...memoizedAssetProps
        });
    }, [
        asset,
        memoizedAssetProps
    ]);
    return isAboveMediumScreen ? /*#__PURE__*/ _jsx(DesktopTableRowContainer, {
        style: {
            paddingLeft: responsiveHorizontalPadding,
            paddingRight: responsiveHorizontalPadding,
            ...style
        },
        "data-testid": "".concat(TABLE_ROW, "-").concat(itemType),
        "data-title": asset.title,
        children: /*#__PURE__*/ _jsx(AssetTableRow, {
            renderNameCell: renderNameCell,
            CustomFieldTableCell: PublicAssetCustomFieldTableCell,
            assetProps: memoizedAssetProps,
            data: asset
        })
    }) : /*#__PURE__*/ _jsx(MobileTableRowContainer, {
        style: {
            paddingLeft: responsiveHorizontalPadding,
            paddingRight: responsiveHorizontalPadding,
            ...style
        },
        children: renderNameCell({
            isSelected: false,
            isHovering: false
        })
    });
}, isEqual);
PublicAssetTableRow.displayName = "PrivateAssetTableRow";
